import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {useCallback} from 'react'

import {useUpdateIssueMilestoneBulkMutation} from './mutations/use-update-issue-milestone-bulk-mutation'
import {MilestonePicker, type MilestonePickerProps} from './MilestonePicker'

export type BulkIssueMilestonePickerProps = Omit<
  MilestonePickerProps,
  'issueId' | 'onIssueUpdate' | 'onSelectionChange'
> &
  SharedBulkActionsItemPickerProps &
  Parameters<typeof useUpdateIssueMilestoneBulkMutation>[0]

export function BulkIssueMilestonePicker({
  onError,
  onCompleted,
  onMutate,
  issuesToActOn,
  ...rest
}: BulkIssueMilestonePickerProps) {
  const {mutate: bulkUpdateMutate} = useUpdateIssueMilestoneBulkMutation({onMutate})
  const onSelectionChange = useCallback(
    (selectedMilestone: MilestonePickerMilestone[]) => {
      const milestoneId =
        selectedMilestone.length === 1 && selectedMilestone[0]?.id !== 'no-milestone' ? selectedMilestone[0]?.id : null
      const clearMilestone = selectedMilestone[0]?.id === 'no-milestone'
      const ids = [...issuesToActOn]
      let input: Parameters<typeof onMutate>[0] | undefined
      if (milestoneId) {
        input = {ids, milestoneId, milestone: selectedMilestone[0]}
      } else if (clearMilestone) {
        input = {ids, clearMilestone}
      }
      if (!input) return

      bulkUpdateMutate(input, {
        onSuccess: response => onCompleted?.(response.data.updateIssuesBulk.jobId),
        onError,
      })
    },
    [bulkUpdateMutate, issuesToActOn, onCompleted, onError],
  )

  return <MilestonePicker onSelectionChange={onSelectionChange} {...rest} />
}

try{ BulkIssueMilestonePicker.displayName ||= 'BulkIssueMilestonePicker' } catch {}